<template>
    <loader showMessage loader v-if="loaders.mainLoader" />
    <div v-else>
        <h4
            v-if="
                !loaders.isLoadingQuestionnaireData && questionnaireId !== null
            "
        >
            {{ questionnaire.title }}
        </h4>
        <div v-if="questionnaireId === null">
            <questionnaires
                :userId="currentUser.id"
                :state="checkState($v.form.questionnaire)"
                :selected.sync="$v.form.questionnaire.$model"
                @change-value="onChangeSelectedQuestionnaire"
            />
        </div>
        <div v-if="evaluated === null">
            <evaluated
                :valuerId="currentUser.id"
                :state="checkState($v.form.evaluated)"
                :selected.sync="$v.form.evaluated.$model"
                :questionnaireId="
                    form.questionnaire ? form.questionnaire.value : null
                "
            />
        </div>
        <div
            v-if="
                !loaders.isLoadingQuestionnaireData &&
                    questionnaire.values.length > 0
            "
            class="mb-1"
        >
            <p class="mb-2">
                <strong> {{ $t('form.label.subjects') + ':' }}</strong>
            </p>
            <div class="d-flex flex-row flex-wrap">
                <div
                    class="mr-2 mb-2"
                    :key="`${value.id}-${questionnaire.id}`"
                    v-for="value in questionnaire.values"
                >
                    <b-link
                        :id="`avatar-${value.id}-${questionnaire.id}`"
                        @click="addValue(value)"
                    >
                        <avatar
                            :size="45"
                            :style="{
                                opacity: selectedValues.includes(value)
                                    ? 1
                                    : 0.4,
                            }"
                            :src="value.picture"
                            :username="value.title"
                        />
                    </b-link>
                    <b-tooltip
                        :target="`avatar-${value.id}-${questionnaire.id}`"
                        placement="top"
                    >
                        {{ value.title }}
                    </b-tooltip>
                </div>
            </div>
        </div>
        <div
            :key="value.id"
            v-for="value in selectedValues"
            style="margin-bottom: 2rem"
        >
            <b-row class="mb-2" no-gutters>
                <b-col>
                    <h5 class="h-100 m-0">{{ value.title }}</h5>
                </b-col>
                <b-col>
                    <b-button
                        v-if="selectedValues.length > 1"
                        class="float-right p-0 h-100"
                        variant="link"
                        @click="removeValue(value)"
                    >
                        {{ $t('form.button.remove') }}
                    </b-button>
                </b-col>
            </b-row>
            <div
                style="margin-bottom: 8px"
                :key="question.$model.key_behavior_id"
                v-for="question in getValueQuestions(value.id)"
            >
                <question
                    :state="checkState(question)"
                    :degrees="question.degrees.$model"
                    :score.sync="question.score.$model"
                    :isNullable="question.is_nullable.$model"
                    :degreeId.sync="question.degree_id.$model"
                    :isRequired="question.is_required.$model"
                    :scaleFormat="question.scale_format.$model"
                    :question="question.key_behavior_title.$model"
                    :notAbleToAnswer.sync="question.not_able_to_answer.$model"
                    :hasScore="
                        question.not_able_to_answer.$model == true ||
                            question.score.$model != null
                    "
                />
            </div>
            <div
                :key="comment.id.$model"
                v-for="comment in getComments(value.id, 'value')"
            >
                <h6>
                    {{
                        comment.$model.title +
                            (comment.$model.is_required ? '*' : '')
                    }}
                </h6>
                <comment
                    :toolbar="toolbar"
                    :value.sync="comment.$model.content"
                    :state="checkState(comment.content)"
                >
                    <span>
                        {{ $t('form.validations.required') }}
                    </span>
                </comment>
            </div>
        </div>
        <div
            v-if="
                !loaders.isLoadingQuestionnaireData && questionnaireHasComments
            "
        >
            <h5>{{ $t('values.generalComments') }}</h5>
            <div
                :key="comment.id.$model"
                v-for="comment in getComments(
                    questionnaire.id,
                    'questionnaire'
                )"
            >
                <h6>
                    {{
                        comment.$model.title +
                            (comment.$model.is_required ? '*' : '')
                    }}
                </h6>
                <comment
                    :toolbar="toolbar"
                    :value.sync="comment.$model.content"
                    :state="checkState(comment.content)"
                >
                    <span>
                        {{ $t('form.validations.required') }}
                    </span>
                </comment>
            </div>
        </div>
        <div>
            <b-form-invalid-feedback
                :state="
                    checkState($v.form.comments) && checkState($v.form.answers)
                "
            >
                {{ $t('form.validations.commentsOrAnswersRequired') }}
            </b-form-invalid-feedback>
        </div>
        <div class="text-muted small">
            {{ $t('form.warning.requiredFields') }}
        </div>
        <div class="float-right">
            <b-button
                class="mr-2"
                size="sm"
                :disabled="isSubmitting"
                @click="$emit('hide')"
            >
                {{ $t('form.button.cancel') }}
            </b-button>
            <b-button
                size="sm"
                variant="primary"
                :disabled="isSubmitting"
                @click="onSubmit"
            >
                <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2" />
                <span v-else> {{ $t('form.button.send') }} </span>
            </b-button>
        </div>
    </div>
</template>
<script>
import Avatar from 'vue-avatar'
import Evaluated from './Evaluated'
import Questionnaires from './Questionnaires'
import Loader from '@/views/components/loader'
import { Question } from '@/views/values/components'
import { Description } from '@/views/components/fields'
import { unionBy } from 'lodash'
import { validationMixin } from 'vuelidate'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { getQuestionnaireToEvaluate } from '@/views/values/api'

export default {
    mixins: [validationMixin],
    components: {
        Avatar,
        Loader,
        Question,
        Evaluated,
        Questionnaires,
        Comment: Description,
    },
    props: {
        isSubmitting: {
            type: Boolean,
            required: true,
        },
        evaluated: {
            type: Object,
            default: () => null,
        },
        valueId: {
            type: Number,
            default: () => null,
        },
        questionnaireId: {
            type: Number,
            default: () => null,
        },
    },
    validations: {
        form: {
            questionnaire: {
                required,
            },
            evaluated: {
                required,
            },
            answers: {
                required: requiredIf(vm => vm.comments.length === 0),
                $each: {
                    question_id: {
                        required,
                    },
                    scale_id: {},
                    is_nullable: {},
                    is_required: {},
                    scale_format: {},
                    not_able_to_answer: {},
                    score: {
                        required: requiredIf(vm => {
                            if (vm.is_required && vm.is_nullable) {
                                return (
                                    vm.score == null &&
                                    vm.not_able_to_answer == false
                                )
                            }

                            if (vm.is_nullable && !vm.is_required) {
                                return false
                            }

                            return vm.is_required
                        }),
                    },
                    value_id: {
                        required,
                    },
                    key_behavior_id: {
                        required,
                    },
                    degree_id: {},
                    degrees: {},
                    key_behavior_title: {},
                },
            },
            comments: {
                required: requiredIf(vm => vm.answers.length === 0),
                $each: {
                    id: {
                        required,
                    },
                    title: {},
                    is_required: {},
                    commentable_id: {
                        required,
                    },
                    commentable_type: {
                        required,
                    },
                    content: {
                        required: requiredIf(vm => vm.is_required),
                    },
                },
            },
        },
    },
    data() {
        return {
            questionnaire: null,
            selectedValues: [],
            form: {
                answers: [],
                comments: [],
                evaluated: null,
                questionnaire: null,
            },
            loaders: {
                mainLoader: false,
                isLoadingQuestionnaireData: true,
            },
            toolbar: [
                'bold',
                'italic',
                'underline',
                'strike',
                { list: 'ordered' },
                { list: 'bullet' },
            ],
        }
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
        questionnaireHasComments() {
            if (this.questionnaire) {
                return (
                    this.getComments(this.questionnaire.id, 'questionnaire')
                        .length > 0
                )
            }
            return false
        },
    },
    watch: {
        questionnaire(newQuestionnaire) {
            if (newQuestionnaire) {
                this.setFormComments()
                this.setQuestionnaireValues()
            }
        },
        selectedValues() {
            if (this.questionnaire) {
                this.setFormComments()
            }
        },
    },
    mounted() {
        if (this.questionnaireId) {
            this.loaders.mainLoader = true
            this.getQuestionnaireToEvaluate(this.questionnaireId).finally(
                () => {
                    if (this.valueId) {
                        const defaultSelectedValue = this.questionnaire.values.find(
                            value => value.id == this.valueId
                        )

                        this.addValue(defaultSelectedValue)
                    }

                    if (this.evaluated) {
                        this.form.evaluated = {
                            value: this.evaluated.id,
                            label: this.evaluated.name,
                        }
                    }

                    this.form.questionnaire = {
                        value: this.questionnaire.id,
                        label: this.questionnaire.title,
                    }
                    this.loaders.mainLoader = false
                }
            )
        }
    },
    methods: {
        onSubmit() {
            if (this.validateForm()) {
                const data = this.mapperFormToApi()
                this.$emit('submit', data)
            }
        },
        mapperFormToApi() {
            const data = {
                valuer_id: this.currentUser.id,
                evaluated_id: this.form.evaluated.value,
                questionnaire_id: this.form.questionnaire.value,
            }

            const answers = this.form.answers.reduce((acc, answer) => {
                if (
                    answer.score !== null ||
                    answer.not_able_to_answer == true
                ) {
                    acc.push({
                        score: answer.score,
                        question_id: answer.question_id,
                    })
                }
                return acc
            }, [])

            const comments = this.form.comments.reduce((acc, comment) => {
                if (comment.content !== null && comment.content !== '') {
                    acc.push({
                        id: comment.id,
                        content: comment.content,
                    })
                }
                return acc
            }, [])

            if (answers.length) {
                data.answers = answers
            }

            if (comments.length) {
                data.comments = comments
            }

            return data
        },
        validateForm() {
            this.$v.$touch()
            return !this.$v.form.$anyError
        },
        checkState(value) {
            return value.$dirty ? !value.$invalid : null
        },
        getQuestionnaireToEvaluate(questionnaireId) {
            this.loaders.isLoadingQuestionnaireData = true
            return getQuestionnaireToEvaluate(questionnaireId)
                .then(questionnaire => {
                    this.questionnaire = questionnaire
                })
                .finally(() => {
                    this.loaders.isLoadingQuestionnaireData = false
                })
        },
        onChangeSelectedQuestionnaire(option) {
            this.setDefaultData(option)
            this.getQuestionnaireToEvaluate(option.value)
            this.$emit('change-questionnaire', option.value)
            this.$v.$reset()
        },
        setDefaultData(option) {
            this.selectedValues = []
            this.form = {
                answers: [],
                comments: [],
                evaluated: null,
                questionnaire: option,
            }
        },
        getValueQuestions(valueId) {
            return Object.keys(this.$v.form.answers.$each.$iter).reduce(
                (acc, key) => {
                    const question = this.$v.form.answers.$each.$iter[key]
                    if (question.$model.value_id == valueId) {
                        acc.push(question)
                    }
                    return acc
                },
                []
            )
        },
        getComments(commentableId, commentableType) {
            return Object.keys(this.$v.form.comments.$each.$iter).reduce(
                (acc, key) => {
                    const comment = this.$v.form.comments.$each.$iter[key]
                    if (
                        comment.$model.commentable_type == commentableType &&
                        comment.$model.commentable_id == commentableId
                    ) {
                        acc.push(comment)
                    }

                    return acc
                },
                []
            )
        },
        addValue(value) {
            if (this.selectedValues.includes(value)) {
                return
            }

            const questions = this.mapperValueQuestionsToForm(value)
            this.form.answers = [...this.form.answers, ...questions]

            this.selectedValues.push(value)
        },
        mapperValueQuestionsToForm(value) {
            return this.questionnaire.questions
                .filter(question => question.value_id == value.id)
                .map(question => {
                    const {
                        id,
                        scale_id,
                        value_id,
                        is_nullable,
                        is_required,
                        scale_format,
                        key_behavior_id,
                    } = question
                    return {
                        scale_id,
                        value_id,
                        is_nullable,
                        is_required,
                        degree_id: '',
                        scale_format,
                        score: null,
                        question_id: id,
                        key_behavior_id,
                        not_able_to_answer: false,
                        degrees: this.getQuestionDegrees(scale_id),
                        key_behavior_title: this.getQuestionTitle(
                            key_behavior_id
                        ),
                    }
                })
        },
        removeValue(value) {
            this.form.answers = this.form.answers.filter(answer => {
                return answer.value_id !== value.id
            })

            this.form.comments = this.form.comments.filter(comment => {
                const isValueComment =
                    comment.commentable_id == value.id &&
                    comment.commentable_type == 'value'
                return isValueComment ? false : true
            })

            this.selectedValues = this.selectedValues.filter(item => {
                return item.id !== value.id
            })
        },
        getQuestionTitle(keyBehaviorId) {
            const keyBehavior = this.questionnaire.key_behaviors.find(
                i => i.id == keyBehaviorId
            )
            return keyBehavior.title
        },
        getQuestionDegrees(scaleId) {
            const scale = this.questionnaire.scales.find(s => s.id == scaleId)
            return scale.degrees
        },
        setFormComments() {
            const valuesIds = this.selectedValues.map(i => i.id)
            this.form.comments = this.questionnaire.questionnaire_has_comments.reduce(
                (acc, comment) => {
                    const isValueComment = comment.commentable_type == 'value'
                    const isQuestionnaireComment =
                        comment.commentable_type == 'questionnaire'
                    const selectedValuesIncludesTheCommentableValue = valuesIds.includes(
                        parseInt(comment.commentable_id)
                    )

                    if (
                        (isValueComment &&
                            selectedValuesIncludesTheCommentableValue) ||
                        isQuestionnaireComment
                    ) {
                        acc.push({
                            ...comment,
                            content: null,
                        })
                    }

                    return acc
                },
                []
            )
        },
        setQuestionnaireValues() {
            const commentableValues = this.questionnaire.questionnaire_has_comments.reduce(
                (acc, comment) => {
                    if (comment.commentable_type == 'value') {
                        acc.push(comment.commentable)
                    }
                    return acc
                },
                [...this.questionnaire.values]
            )

            this.questionnaire.values = unionBy(commentableValues, 'id')
        },
    },
}
</script>
<style>
b,
strong {
    font-weight: 600;
}
</style>
